import { Divider, TextField, Typography } from "@mui/material";
import { CustomButton } from "components/button/styled.components";
import HeaderWithFilter from "components/headerwithfilter";
import FullScreenLoader from "hoc/loader/loader";
import keys from "locale/keys";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import Coin from "../../../../assets/icons/coin.svg";
import StarIcon from "../../../../assets/icons/star_icon.svg";
import wallet from "../../../../assets/icons/wallet.svg";
import QuestionCompletionDialog from "../../../../components/questioncompletiondialog/QuestionCompletionDialog";
import {
  getSurveyProgessBarApi,
  getSurveyQuestionsApi,
  submitSurveyProfilerApi,
} from "../../../../data/slices/surveyquestions/api";
import { WalletDetailsActions } from "../../../../data/slices/wallet";
import {
  STORAGE_SELECTED_MENU,
  STORAGE_USER_ID,
  SURVEY_QUESTIONS,
} from "../../../../utils/constants";
import { surveyQuestionsMapper } from "../../../../utils/mappers/SurveyQuestionsMapper";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "../../../../utils/Utils";
import {
  ButtonContainer,
  CategoryIcon,
  CategoryText,
  CategoryTextContainer,
  CheckBox,
  ErrorText,
  NumberOfQuestions,
  OptionBox,
  ProgressBarContainer,
  ProgressBarFill,
  QuestionTitle,
  RadioBox,
  RemainingQuestions,
  ResponseMsg,
  RewardsContainer,
  RewardText,
  RewardTextContainer,
  SurveyContainer,
  SurveyQuestionMainContainer,
  TopSection,
} from "./styled.components";

const SurveyQuestionContainer = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const categoryId = parseInt(location.state.categoryId);
  const categoryName = location.state.category;
  const userId = getFromLocalStorage(STORAGE_USER_ID);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number[] | string>>({});
  const [error, setError] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState<number[]>([]);
  const [questions, setQuestions] = useState<any>({ Questions: [] });
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [progressBar, setProgressBar] = useState(0);
  const [remainingQuestionsReward, setRemainingQuestionsReward] = useState(0);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const fetchQuestions = async () => {
    try {
      setIsLoading(true);
      const response = await getSurveyQuestionsApi({
        categoryId,
        userId,
      });

      const responseData = response.data;

      setTotalQuestions(responseData?.total);

      if (responseData?.total === 0) {
        navigation("/dashboard/surveyprofile");
      }
      const mappedQuestions = surveyQuestionsMapper(
        responseData.Questions || [],
      );
      setQuestions({ Questions: mappedQuestions || [] });
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProgressBarStatus = async () => {
    try {
      const response = await getSurveyProgessBarApi({ userId });
      setProgressBar((10 - response?.data?.remainingForReward) * 10);
      setRemainingQuestionsReward(10 - response?.data?.remainingForReward);
      if (response?.data?.remainingToday === 0) {
        setDailyLimitReached(true);
        setShowCompletionDialog(true);
      }
    } catch (error) {
      console.error("Error fetching progress:", error);
    }
  };

  // Initial data loading - fetch all questions once
  useEffect(() => {
    fetchQuestions();
    fetchProgressBarStatus();

    // When categoryId changes, fetch new questions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // Refresh data after successful submission
  useEffect(() => {
    if (shouldRefresh) {
      fetchQuestions();
      fetchProgressBarStatus();
      setShouldRefresh(false);

      if (totalQuestions === 0) {
        navigation(-1);
      }
    }

    // When shouldRefresh changes it starts a new cycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh]);

  // Ensure currentIndex is valid
  useEffect(() => {
    if (
      questions?.Questions?.length > 0 &&
      currentIndex >= questions.Questions.length
    ) {
      setCurrentIndex(0);
    }

    // When questions change, reset currentIndex
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, currentIndex]);

  const currentQuestion = questions.Questions[currentIndex];

  const handleSelect = (answerId: number, answerText: string) => {
    if (!currentQuestion) return;

    setError(false);
    const questionId = currentIndex;
    const isMultiSelect =
      currentQuestion.questionType.toLowerCase() === "multi";

    setAnswers((prev) => {
      if (isMultiSelect) {
        const existing = (
          Array.isArray(prev[questionId]) ? prev[questionId] : []
        ) as number[];
        const updated = existing.includes(answerId)
          ? existing.filter((id) => id !== answerId)
          : [...existing, answerId];
        return { ...prev, [questionId]: updated };
      }
      return { ...prev, [questionId]: [answerId] };
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentQuestion) return;
    setError(false);
    const questionId = currentIndex;
    const { value } = event.target;

    if (currentQuestion.questionType.toLowerCase() === "number") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setAnswers((prev) => ({ ...prev, [questionId]: numericValue }));
    } else {
      setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }
  };

  const handleNext = async () => {
    if (!currentQuestion) return;

    const currentAnswer = answers[currentIndex];
    const questionType = currentQuestion.questionType.toLowerCase();

    const isAnswerMissing =
      questionType === "text" || questionType === "number"
        ? !currentAnswer ||
          typeof currentAnswer !== "string" ||
          currentAnswer.trim() === ""
        : !Array.isArray(currentAnswer) || currentAnswer.length === 0;

    if (isAnswerMissing) {
      setError(true);
      return;
    }

    if (dailyLimitReached) {
      setShowCompletionDialog(true);
      return;
    }

    try {
      const answerPayload = answers[currentIndex];
      const type = currentQuestion.questionType?.toLowerCase();

      // Send responseText for text/number, otherwise answerIds
      const payload =
        type === "text" || type === "number"
          ? {
              userId,
              questionId: currentQuestion.id,
              responseText: String(answerPayload),
            }
          : {
              userId,
              questionId: currentQuestion.id,
              answerIds: answerPayload,
            };

      await submitSurveyProfilerApi(payload as any);

      const isNewCompletion = !completedQuestions.includes(currentIndex);
      const newCompletedQuestions = isNewCompletion
        ? [...completedQuestions, currentIndex]
        : completedQuestions;

      if (isNewCompletion) {
        setCompletedQuestions(newCompletedQuestions);
      }

      // Show completion dialog when needed
      if (remainingQuestionsReward === 9 || progressBar === 90) {
        setShowCompletionDialog(true);
        // Refresh wallet details immediately when 10th question is completed
        dispatch(WalletDetailsActions.request());
      } else if (currentIndex < questions.Questions.length - 1) {
        // Move to the next question if available
        setCurrentIndex(currentIndex + 1);
      }

      // Update progress bar after submission
      setShouldRefresh(true);
      setError(false);
    } catch (error) {
      console.error("Error submitting survey profiler:", error);
    }
  };

  const handleCloseCompletionDialog = () => {
    setShowCompletionDialog(false);
    if (dailyLimitReached) {
      setDailyLimitReached(false);
      navigation(-1);
    }
  };

  const handleChangeCategory = () => {
    setShowCompletionDialog(false);
    navigation(-1);
  };

  const handleContinueQuestions = () => {
    setShowCompletionDialog(false);
    // If there are more questions, continue from where we left off
    if (currentIndex < questions.Questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // If we've completed all questions, go back to the first one
      setCurrentIndex(0);
    }
    setShouldRefresh(true); // Update progress bar
  };

  const handleDailyLimitReached = () => {
    setShowCompletionDialog(false);
    saveToLocalStorage(STORAGE_SELECTED_MENU, "0");
    navigation("/");
  };

  return (
    <SurveyQuestionMainContainer>
      {isLoading && <FullScreenLoader />}
      <HeaderWithFilter
        title={t(keys.categories)}
        showBackButton={true}
        showFilter={false}
        onBackButtonClickHandler={() => navigation(-1)}
      />
      <Divider />
      <SurveyContainer>
        <TopSection>
          <RewardsContainer>
            <CategoryTextContainer>
              <CategoryIcon src={wallet} />
              <CategoryText>{categoryName}</CategoryText>
            </CategoryTextContainer>
            <RewardTextContainer>
              <CategoryIcon src={Coin} />
              <RewardText
                dangerouslySetInnerHTML={{ __html: t(keys.get_two_coins) }}
              />
            </RewardTextContainer>
          </RewardsContainer>
          <RemainingQuestions>{`${totalQuestions} Remaining`}</RemainingQuestions>
        </TopSection>
        {questions?.Questions?.length > 0 && currentQuestion && !isLoading && (
          <>
            <NumberOfQuestions>
              {SURVEY_QUESTIONS.QUESTIONS} {progressBar / 10}/10
            </NumberOfQuestions>
            <ProgressBarContainer>
              <ProgressBarFill progress={progressBar} />
            </ProgressBarContainer>

            <QuestionTitle>{currentQuestion.questionText || ""}</QuestionTitle>

            {["text", "number"].includes(
              currentQuestion.questionType?.toLowerCase(),
            ) ? (
              <TextField
                fullWidth
                variant="outlined"
                value={
                  typeof answers[currentIndex] === "string"
                    ? (answers[currentIndex] as string)
                    : ""
                }
                onChange={handleInputChange}
                error={error}
                inputProps={{
                  inputMode:
                    currentQuestion.questionType?.toLowerCase() === "number"
                      ? "numeric"
                      : "text",
                  pattern:
                    currentQuestion.questionType?.toLowerCase() === "number"
                      ? "[0-9]*"
                      : undefined,
                }}
              />
            ) : (
              currentQuestion.options?.map((answer) => (
                <OptionBox
                  key={answer.id}
                  onClick={() => handleSelect(answer.id, answer.optionText)}
                  hasError={error}
                >
                  {currentQuestion.questionType?.toLowerCase() === "multi" ? (
                    <CheckBox
                      checked={
                        (answers[currentIndex] as number[])?.includes(
                          answer.id,
                        ) || false
                      }
                    />
                  ) : (
                    <RadioBox
                      checked={
                        (answers[currentIndex] as number[])?.includes(
                          answer.id,
                        ) || false
                      }
                    />
                  )}
                  <Typography>{answer.optionText}</Typography>
                </OptionBox>
              ))
            )}
          </>
        )}
        {error && <ErrorText>{SURVEY_QUESTIONS.ERROR_MESSAGE}</ErrorText>}
        <ButtonContainer>
          <ResponseMsg>{SURVEY_QUESTIONS.NOTE}</ResponseMsg>
          <CustomButton
            type="button"
            variant="contained"
            margintop="0px"
            bgcolor="#000000"
            textcolor="white"
            buttonsize={1}
            onClick={handleNext}
            disabled={isLoading}
          >
            {SURVEY_QUESTIONS.NEXT_BUTTON_LABEL}
          </CustomButton>
        </ButtonContainer>
      </SurveyContainer>

      <QuestionCompletionDialog
        show={showCompletionDialog}
        dismiss={handleCloseCompletionDialog}
        heading={
          dailyLimitReached
            ? SURVEY_QUESTIONS.DIALOG_LIMIT_REACHED_HEADING
            : SURVEY_QUESTIONS.DIALOG_HEADING
        }
        subheading={
          dailyLimitReached
            ? SURVEY_QUESTIONS.DIALOG_LIMIT_REACHED_DESCRIPTION
            : SURVEY_QUESTIONS.DIALOG_DESCRIPTION
        }
        imageSrc={StarIcon}
        earnedCoins={2}
        onChangeCategoryClick={handleChangeCategory}
        onNextQuestionClick={
          dailyLimitReached ? handleDailyLimitReached : handleContinueQuestions
        }
        changeCategoryText={SURVEY_QUESTIONS.CHANGE_CATEGORY_BUTTON_LABEL}
        nextQuestionText={
          dailyLimitReached
            ? SURVEY_QUESTIONS.HOME_BUTTON_LABEL
            : SURVEY_QUESTIONS.NEXT_QUESTION_BUTTON_LABEL
        }
        isImageDisabled={!dailyLimitReached}
        isClappingHandDisabled={dailyLimitReached}
        isCoinsTextDisabled={!dailyLimitReached}
        isChangeCategoryButtonDisabled={dailyLimitReached}
      />
    </SurveyQuestionMainContainer>
  );
};

export default SurveyQuestionContainer;
